/**
 * Base Strategy Class
 * 
 * This file provides a base structure for all topic strategies to follow.
 * Each strategy will implement three phases:
 * 1. InitPhase: Send default prompt and guidance to the backend
 * 2. SuggestionsPhase: Leverage data to provide agent suggestions
 * 3. ConversationPhase: Align with the user through conversation
 */

import { runAIMatches } from '../api';
import generateEnhancedGuidance from './enhancedGuidance';

/**
 * Enum for strategy phases
 */
export const StrategyPhase = {
  INIT: 'INIT_PHASE',
  SUGGESTIONS: 'SUGGESTIONS_PHASE',
  CONVERSATION: 'CONVERSATION_PHASE'
};

/**
 * Create a base strategy function that implements the phase-based approach
 * @param {Object} options - Configuration options for the strategy
 * @param {string} options.topicName - The name of the topic (e.g., 'skills', 'experience')
 * @param {Function} options.initPhaseHandler - Handler for the init phase
 * @param {Function} options.suggestionsPhaseHandler - Handler for the suggestions phase
 * @param {Function} options.conversationPhaseHandler - Handler for the conversation phase
 * @param {Function} options.renderComponentHandler - Handler for rendering UI components (optional)
 * @returns {Function} A strategy function that follows the phase-based approach
 */
export const createBaseStrategy = ({
  topicName,
  initPhaseHandler,
  suggestionsPhaseHandler,
  conversationPhaseHandler,
  renderComponentHandler = null
}) => {
  /**
   * The strategy function that will be called by the topic decision engine
   * @param {Object} decision - Current decision object
   * @param {Array} messages - Current conversation messages
   * @param {Function} setMessages - Function to update messages state
   * @returns {Object} Updated decision object
   */
  return async (decision, messages, setMessages) => {
    // Log the strategy execution
    console.log(`Executing ${topicName} strategy`);
    
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nExecuting ${topicName} strategy...`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Determine the current phase based on the decision object
    // If no phase is specified, start with the INIT phase
    const currentPhase = decision.strategyPhase || StrategyPhase.INIT;
    console.log(`Current phase for ${topicName} strategy: ${currentPhase}`);
    
    // Update thinking accordion with the current phase
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nCurrent phase: ${currentPhase}`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    try {
      // Check if we need to render a UI component
      if (decision.actionType === 'RENDER_COMPONENT' && renderComponentHandler) {
        console.log(`Rendering component for ${topicName} strategy`);
        return await renderComponentHandler(decision, messages, setMessages);
      }
      
      // Execute the appropriate phase handler based on the current phase
      switch (currentPhase) {
        case StrategyPhase.INIT:
          console.log(`Executing init phase for ${topicName} strategy`);
          return await initPhaseHandler(decision, messages, setMessages);
          
        case StrategyPhase.SUGGESTIONS:
          console.log(`Executing suggestions phase for ${topicName} strategy`);
          return await suggestionsPhaseHandler(decision, messages, setMessages);
          
        case StrategyPhase.CONVERSATION:
          console.log(`Executing conversation phase for ${topicName} strategy`);
          return await conversationPhaseHandler(decision, messages, setMessages);
          
        default:
          console.warn(`Unknown phase "${currentPhase}" for ${topicName} strategy, falling back to init phase`);
          return await initPhaseHandler(decision, messages, setMessages);
      }
    } catch (error) {
      console.error(`Error in ${topicName} strategy:`, error);
      
      // Update thinking accordion with the error
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking}\nError in ${topicName} strategy: ${error.message}`
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      // Return the original decision on error
      return decision;
    }
  };
};

/**
 * Helper function to generate default guidance for a topic
 * @param {string} topicName - The name of the topic
 * @returns {string} Default guidance for the topic
 */
export const generateDefaultGuidance = (topicName) => {
  const guidanceMap = {
    experience: `Focus on understanding the years of experience needed for this role. Ask clarifying questions about the level of seniority required, whether they need someone with specific industry experience, and how important overall experience is compared to specific skills.`,
    
    skills: `Focus on identifying the key technical and soft skills required for this role. Ask about must-have skills versus nice-to-have skills, and try to understand the relative importance of different skills. Probe for specific technologies, tools, or methodologies that candidates should be familiar with.`,
    
    location: `Focus on understanding the location requirements for this role. Ask if remote work is an option, if they're looking for candidates in specific regions or time zones, and if relocation assistance is available. Clarify if there are any visa or work authorization requirements.`,
    
    title: `Focus on refining the job title for this role. Ask about the level of seniority, the team structure, and how this role fits into the organization. Suggest alternative titles that might better reflect the responsibilities and attract the right candidates.`,
    
    company: `Focus on understanding the company culture and values. Ask about the company's mission, the team structure, and what makes someone successful in their organization. Probe for information about the company's stage, size, and industry.`,
    
    education: `Focus on understanding the educational requirements for this role. Ask about degree requirements, certifications, or alternative qualifications that might be acceptable. Clarify if specific educational backgrounds are preferred or required.`
  };
  
  return guidanceMap[topicName] || 
    `Focus on gathering information about ${topicName} for this role. Ask clarifying questions to understand the requirements and preferences.`;
};

/**
 * Default implementation of the init phase handler
 * @param {string} topicName - The name of the topic
 * @returns {Function} A handler function for the init phase
 */
export const createDefaultInitPhaseHandler = (topicName) => {
  return async (decision, messages, setMessages) => {
    console.log(`Default init phase handler for ${topicName}`);
    
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nInitializing ${topicName} discovery...`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Generate default guidance for this topic
    const defaultGuidance = generateDefaultGuidance(topicName);
    
    // Create a decision with suggestions phase
    const suggestionsDecision = {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: defaultGuidance,
      strategyPhase: StrategyPhase.SUGGESTIONS
    };
    
    // Log that we're transitioning directly to suggestions phase
    console.log(`${topicName} InitPhase: Directly transitioning to SuggestionsPhase`);
    
    // Get the default suggestions phase handler
    const suggestionsPhaseHandler = createDefaultSuggestionsPhaseHandler(topicName);
    
    // Immediately call the suggestions phase handler
    return await suggestionsPhaseHandler(suggestionsDecision, messages, setMessages);
  };
};

/**
 * Default implementation of the suggestions phase handler
 * @param {string} topicName - The name of the topic
 * @returns {Function} A handler function for the suggestions phase
 */
export const createDefaultSuggestionsPhaseHandler = (topicName) => {
  return async (decision, messages, setMessages) => {
    console.log(`Default suggestions phase handler for ${topicName}`);
    
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nGenerating suggestions for ${topicName}...`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    try {
      // Get AI matches to generate suggestions
      const aiMatchesResult = await runAIMatches(messages);
      
      if (!aiMatchesResult) {
        console.warn(`No AI matches result for ${topicName}`);
        return {
          ...decision,
          actionType: 'SEND_TO_BACKEND',
          strategyPhase: StrategyPhase.CONVERSATION
        };
      }
      
      // Generate enhanced guidance with Findem Magic explanations
      const enhancedGuidance = await generateEnhancedGuidance(
        aiMatchesResult,
        decision,
        topicName
      );
      
      // Update thinking accordion
      if (setMessages) {
        setMessages(prev => {
          const updatedMessages = prev.map(msg =>
            msg.role === 'AI' && msg.content === 'Typing...'
              ? {
                  ...msg,
                  thinking: `${msg.thinking}\nGenerated suggestions for ${topicName}`
                }
              : msg
          );
          return updatedMessages;
        });
      }
      
      // Update decision to move to the conversation phase
      return {
        ...decision,
        actionType: 'SEND_WITH_GUIDANCE',
        agentGuidance: enhancedGuidance,
        aiMatchesResult,
        strategyPhase: StrategyPhase.CONVERSATION
      };
    } catch (error) {
      console.error(`Error generating suggestions for ${topicName}:`, error);
      
      // Fall back to conversation phase without suggestions
      return {
        ...decision,
        actionType: 'SEND_TO_BACKEND',
        strategyPhase: StrategyPhase.CONVERSATION
      };
    }
  };
};

/**
 * Default implementation of the conversation phase handler
 * @param {string} topicName - The name of the topic
 * @returns {Function} A handler function for the conversation phase
 */
export const createDefaultConversationPhaseHandler = (topicName) => {
  return async (decision, messages, setMessages) => {
    console.log(`Default conversation phase handler for ${topicName}`);
    
    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nContinuing conversation about ${topicName}...`
              }
            : msg
        );
        return updatedMessages;
      });
    }
    
    // Generate guidance for the conversation phase that emphasizes alignment and confirmation
    const conversationGuidance = `Continue discussing ${topicName} requirements with the user. Your goal is to align with the user on this topic and get an EXPLICIT confirmation before moving on.

1. First, acknowledge the user's input about ${topicName} and ask any clarifying questions needed.
2. Then, summarize what you've understood about their ${topicName} requirements in a clear, concise way.
3. Finally, ask EXPLICITLY if they're ready to move on to the next topic using a clear question like:
   - "Does that summary of your ${topicName} requirements look accurate? If so, shall we move on to discussing the next topic?"
   - "Have I captured your ${topicName} needs correctly? If yes, we can proceed to the next area."
   - "Is there anything else about ${topicName} you'd like to discuss, or are you ready to move forward?"

IMPORTANT: Do not change topics until the user has EXPLICITLY confirmed they're ready to move on. Short responses that provide more information about ${topicName} should NOT be treated as confirmation to change topics.`;
    
    // In the conversation phase, we pass the user's message to the backend with alignment guidance
    return {
      ...decision,
      actionType: 'SEND_TO_BACKEND',
      agentGuidance: decision.agentGuidance || conversationGuidance,
      strategyPhase: StrategyPhase.CONVERSATION
    };
  };
};

/**
 * Create a default strategy for a topic
 * @param {string} topicName - The name of the topic
 * @returns {Function} A strategy function with default implementations of all phases
 */
export const createDefaultStrategy = (topicName) => {

  return createBaseStrategy({
    topicName,
    initPhaseHandler: createDefaultInitPhaseHandler(topicName),
    suggestionsPhaseHandler: createDefaultSuggestionsPhaseHandler(topicName),
    conversationPhaseHandler: createDefaultConversationPhaseHandler(topicName)
  });
};

export default {
  createBaseStrategy,
  createDefaultStrategy,
  createDefaultInitPhaseHandler,
  createDefaultSuggestionsPhaseHandler,
  createDefaultConversationPhaseHandler,
  generateDefaultGuidance,
  StrategyPhase
};
